import React from 'react'
import styled from 'styled-components'
import PaneBody from 'components/Common/Pane/PaneBody'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Heading from 'components/Luxkit/Typography/Heading'
import Modal from 'components/Luxkit/Modal/Modal'
import Spacing from 'components/Common/Spacing'
import List from 'components/Common/List'
import { TermsAndConditions } from 'components/Booking/Payment/Links'
import { connect } from 'react-redux'
import { getDepositServiceFeeConfig } from 'checkout/selectors/featureConfig/deposit'
import moment from 'moment'
import { INTL_DMY_CASUAL_SHORT_FORMAT } from 'constants/dateFormats'
import { useIntlDateFormatter } from 'lib/dateUtils'
import { getFlexibleCancellationDays, isFlexibleCancellationPolicy } from 'lib/cancellationPolicy'
import { FOURTEEN_DAYS_BEFORE_CHECK_IN, SEVEN_DAYS_AFTER_PURCHASE } from 'constants/cancellationPolicies'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { rem } from 'polished'
import Hr from 'components/Common/Hr'
import VerticalSpacer from '../Spacing/VerticalSpacer'
import FormatCurrency from '../FormatCurrency'

const CancellationTermsWrapper = styled.div`
  display: grid;
  grid-column-gap: ${rem(18)};
  margin-top: ${rem(12)};

  ${mediaQueryUp.sm} {
    grid-template-rows: 1fr;
    grid-template-columns: 1fr;
  }
`

const TermsGrid = styled.div`
  display: grid;
  grid-column-gap: ${rem(16)};
  grid-row-gap: ${rem(18)};
  grid-template-columns: 1fr 2fr;

  ${mediaQueryUp.sm} {
    grid-template-columns: 1fr 1fr;
  }
`

interface Props {
  isPolicyOpen: boolean;
  closePolicy: () => void;
  depositPercentage: number | undefined;
  remainingBalance?: number;
  depositAmount?: number;
  cancellationPolicy?: App.OfferCancellationPolicy;
  propertyTimezoneOffset?: number;
  checkInDate?: string | moment.Moment;
  depositPaymentDueDaysLeft?: number;
}

interface MappedProps {
  serviceFee: number;
}

function PayInInstalmentsMoreInfoModal(props : Props & MappedProps) {
  const {
    isPolicyOpen,
    closePolicy,
    depositPercentage,
    remainingBalance,
    depositAmount,
    serviceFee,
    cancellationPolicy,
    propertyTimezoneOffset,
    checkInDate,
    depositPaymentDueDaysLeft,
  } = props

  const intlDateFormatter = useIntlDateFormatter()

  const days = cancellationPolicy ? getFlexibleCancellationDays(cancellationPolicy) : 21
  let startBasedOnToday = propertyTimezoneOffset ? moment().utcOffset(propertyTimezoneOffset).add(SEVEN_DAYS_AFTER_PURCHASE, 'days') : moment().add(SEVEN_DAYS_AFTER_PURCHASE, 'days')
  let startBasedOnCheckIn = checkInDate && (propertyTimezoneOffset ? moment(checkInDate).utcOffset(propertyTimezoneOffset).subtract(days, 'days') : moment(checkInDate).subtract(days, 'days'))

  const changeOfMindDeadline = checkInDate && propertyTimezoneOffset ? moment(checkInDate).utcOffset(propertyTimezoneOffset).subtract(FOURTEEN_DAYS_BEFORE_CHECK_IN, 'days') : moment(checkInDate).subtract(FOURTEEN_DAYS_BEFORE_CHECK_IN, 'days')
  startBasedOnToday = startBasedOnToday.isBefore(changeOfMindDeadline) ? startBasedOnToday : changeOfMindDeadline

  const cancellationAfterChangeOfMindPeriod = checkInDate && startBasedOnToday.isBefore(changeOfMindDeadline) && moment().add(SEVEN_DAYS_AFTER_PURCHASE, 'days').isBefore(moment(checkInDate).subtract(days, 'days'))
  startBasedOnCheckIn = cancellationAfterChangeOfMindPeriod ? startBasedOnCheckIn : startBasedOnToday

  return <Modal title="How do deposits work?" isOpen={isPolicyOpen} onClose={closePolicy}>
    <PaneBody size="zero">
      <VerticalSpacer gap={24}>
        <BodyText variant="medium">
          Secure select packages with a ${depositPercentage}% deposit today (excluding flights, insurance and experiences add-ons) and pay the rest with your preferred payment plan
          {depositPaymentDueDaysLeft && ` ${depositPaymentDueDaysLeft} days prior to your check-in date`}.
        </BodyText>

        <BodyText variant="medium">
          <VerticalSpacer gap={8}>
            <Heading variant="heading6">
              Choose your preferred payment plan
            </Heading>
            <div>Enjoy the freedom to pay the remaining balance {remainingBalance && <span>of <FormatCurrency value={remainingBalance}/></span>} as:</div>
            <List>
              <li>A single payment</li>
              <li>Weekly payments</li>
              <li>Fortnightly payments</li>
              <li>Monthly payments</li>
            </List>
            <div>Each payment will be automatically charged to your original payment method. For weekly, fortnightly and monthly payments you will receive an email summary of any upcoming payments.</div>
          </VerticalSpacer>
        </BodyText>

        <BodyText variant="medium">
          <Heading variant="heading6">
            Service fee
          </Heading>
          <Spacing mt={8} />
          A non-refundable service fee of {serviceFee}% of the remaining amount owing {remainingBalance && <span>(<FormatCurrency value={remainingBalance} />)</span>} on your accommodation will be charged at the time of booking along with the initial {depositPercentage}% deposit{depositAmount && <span> (<FormatCurrency value={depositAmount}/>)</span>}.
        </BodyText>

        <BodyText variant="medium">
          <Heading variant="heading6">
            Cancellation policy
          </Heading>
          <Spacing mt={8} />
          {!cancellationPolicy &&
          'If you decide to cancel your accommodation within the Free Cancellation Policy time period you will receive your deposit and any payments paid to date to your original payment method or credit (excl. service fee).'
          }

          {cancellationPolicy &&
            <>
              Please see full cancellation breakdown below.
              <CancellationTermsWrapper>
                <TermsGrid>
                  {!checkInDate &&
                    <BodyText variant="medium" weight="semi-bold" as="span">
                      Up to 7 days after purchase
                    </BodyText>
                  }
                  {checkInDate &&
                    <BodyText variant="medium" weight="semi-bold" as="span">
                      Cancel until {intlDateFormatter(startBasedOnToday, INTL_DMY_CASUAL_SHORT_FORMAT)}
                    </BodyText>
                  }
                  <BodyText variant="medium" as="span">
                    100% refund of deposit and payments paid to date to your original payment method or credit (excl. service fee)
                  </BodyText>
                </TermsGrid>
                <Hr size="md"/>
                {isFlexibleCancellationPolicy(cancellationPolicy) &&
                  <>
                    {(cancellationAfterChangeOfMindPeriod && startBasedOnCheckIn) &&
                      <>
                        <TermsGrid>
                          <BodyText variant="medium" weight="semi-bold" as="span">
                            Cancel until {intlDateFormatter(startBasedOnCheckIn, INTL_DMY_CASUAL_SHORT_FORMAT)}
                          </BodyText>
                          <BodyText variant="medium" as="span">
                            100% refund of deposit and payments paid to date to Luxury Escapes credit (excl. service fee)
                          </BodyText>
                        </TermsGrid>
                        <Hr size="md"/>
                      </>
                  }
                    {(!cancellationAfterChangeOfMindPeriod && !checkInDate) &&
                      <>
                        <TermsGrid>
                          <BodyText variant="medium" weight="semi-bold" as="span">
                            Up to {days + 1} days prior to check-in
                          </BodyText>
                          <BodyText variant="medium" as="span">
                            100% refund of deposit and payments paid to date to Luxury Escapes credit (excl. service fee)
                          </BodyText>
                        </TermsGrid>
                        <Hr size="md"/>
                      </>
                    }
                  </>
                }
                <TermsGrid>
                  {!checkInDate && <BodyText variant="medium" weight="semi-bold" as="span">
                    From {days} days until check-in
                  </BodyText>}
                  {checkInDate && startBasedOnCheckIn && <BodyText variant="medium" weight="semi-bold" as="span">
                    Cancel after {intlDateFormatter(startBasedOnCheckIn, INTL_DMY_CASUAL_SHORT_FORMAT)}
                  </BodyText>}
                  <BodyText variant="medium" as="span">
                    Non-refundable
                  </BodyText>
                </TermsGrid>
              </CancellationTermsWrapper>
            </>
          }
        </BodyText>

        <BodyText variant="medium">
          <Heading variant="heading6">
            Failed payments
          </Heading>
          <Spacing mt={8} />
          Failure to complete payments will result in the cancellation of your booking, and all payments will be refunded to Luxury Escapes credit (excl. service fee).
        </BodyText>

        <BodyText variant="medium">
          View full <TermsAndConditions variant="black" />.
        </BodyText>
      </VerticalSpacer>
    </PaneBody>
  </Modal>
}

const mapStateToProps = (state: App.State) => ({
  serviceFee: getDepositServiceFeeConfig(state).percentage,
})

export default connect(mapStateToProps)(PayInInstalmentsMoreInfoModal)
